import { useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface ConnectClientFormProps {
  onClientConnected: () => void;
}

const ConnectClientForm = ({ onClientConnected }: ConnectClientFormProps) => {
  const [clientEmail, setClientEmail] = useState<string>("");
  const [clientKey, setClientKey] = useState<string>("");
  const [connectingClient, setConnectingClient] = useState<boolean>(false);

  const handleConnectClient = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!clientEmail.trim()) {
      toast.error("Please enter a client email");
      return;
    }

    if (!clientKey.trim()) {
      toast.error("Please enter the client's connection key");
      return;
    }
    
    setConnectingClient(true);
    
    try {
      console.log(`Attempting to connect with client: ${clientEmail.trim()}`);
      
      // First get the user_id from user_profiles using email
      let { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('user_id, connection_key')
        .eq('email', clientEmail.trim())
        .maybeSingle();
      
      if (profileError) {
        console.error("Error finding client:", profileError);
        throw new Error("Error looking up client. Please try again.");
      }
      
      // If no profile found by email, try searching by connection key
      if (!profileData) {
        const { data: userProfiles, error: searchError } = await supabase
          .from('user_profiles')
          .select('user_id, connection_key, email')
          .eq('connection_key', clientKey.trim());
          
        if (searchError) {
          console.error("Error searching by connection key:", searchError);
          throw new Error("Client not found. Please verify the email and connection key.");
        }
        
        if (!userProfiles || userProfiles.length === 0) {
          throw new Error("Client not found. Please verify the email and connection key.");
        }
        
        // Use the first matching profile with this connection key
        profileData = userProfiles[0];
        console.log("Found client by connection key:", profileData);
      } else if (profileData.connection_key !== clientKey.trim()) {
        throw new Error("The connection key is incorrect. Please verify and try again.");
      }

      const clientId = profileData.user_id;
      console.log("Found valid client to connect with:", clientId);
      
      // Get therapist ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("You must be logged in to connect with a client");
      }
      
      const therapistId = user.id;
      console.log("Therapist ID:", therapistId);
      console.log("Client ID:", clientId);
      
      // Use the dedicated RPC function to establish the relationship
      // This bypasses RLS since it runs with SECURITY DEFINER privileges
      const { data, error } = await supabase.rpc(
        'connect_client_to_therapist',
        { 
          client_email: clientEmail.trim()
        }
      );
      
      if (error) {
        console.error("Error calling connect_client_to_therapist:", error);
        throw new Error(`Failed to connect with client: ${error.message}`);
      }
      
      console.log("Connection successful:", data);
      toast.success(`Successfully connected with client`);
      setClientEmail("");
      setClientKey("");
      onClientConnected();
    } catch (error: any) {
      console.error("Error connecting client:", error);
      toast.error(error.message || "Failed to connect with client. Make sure the connection key is correct.");
    } finally {
      setConnectingClient(false);
    }
  };

  return (
    <div className="space-y-4 animate-fadeIn">
      <Card className="bg-white backdrop-blur-sm border border-[#CCCCCC]">
        <CardHeader className="bg-gradient-to-r from-[#E8F0F8] to-white rounded-t-lg">
          <CardTitle>Connect with a Client</CardTitle>
          <CardDescription>
            Enter the client's email address and connection key to establish a connection
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleConnectClient} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="clientEmail">Client Email Address</Label>
              <Input
                id="clientEmail"
                type="email"
                placeholder="client@example.com"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                required
                className="border-[#CCCCCC] focus:border-[#3BAFDA] transition-all duration-300"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="clientKey">Client Connection Key</Label>
              <Input
                id="clientKey"
                type="text"
                placeholder="Enter connection key"
                value={clientKey}
                onChange={(e) => setClientKey(e.target.value)}
                required
                className="border-[#CCCCCC] focus:border-[#3BAFDA] transition-all duration-300"
              />
            </div>
            <Button 
              type="submit" 
              disabled={connectingClient}
              className="bg-[#3BAFDA] hover:bg-[#2a9fc4] text-white transition-all duration-300"
            >
              {connectingClient ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Connect with Client
                </>
              )}
            </Button>
          </form>
        </CardContent>
        <CardFooter className="bg-[#E8F0F8]/50 border-t border-[#CCCCCC]">
          <p className="text-sm text-muted-foreground">
            Note: The connection key is required to establish a secure connection. Activity status will always be visible, but journal entries will only be shown based on the client's privacy settings.
          </p>
        </CardFooter>
      </Card>
    </div>
  );
};

export default ConnectClientForm;
